import * as React from 'react';
import { Expander } from 'sos-common/js/components/Expander/Expander';
import { CheckBox } from 'sos-common/js/components/FormControls/CheckBox';
import { TextInput } from 'sos-common/js/components/FormControls/TextInput';
import { setInnerHtml } from 'sos-common/js/utils/helpers';
import thousands from 'thousands';
import { SectionClass, SectionProps, SectionState, FormType, FieldName } from '../PublicForm.types';
import { EpiserverProperty } from 'sos-common/js/utils/episerver.property';
import { FormField } from 'sos-common/js/commonTypes/formField.types';
import { Yrke } from '../../AnsokanForms/LegAnsokanForm.types';
import { MAX_LENGTH_NUM } from '../publicform.validation.service';

export interface IBetalningSectionProps extends SectionProps {
  kostnad: number;
  activeYrke: Yrke;
  specialistCostInformation?: string;
  isOutsideEuEes?: boolean;
  isAssessment?: boolean;
}

export interface IBetalningSectionState extends SectionState {
  harBetalt: boolean;
}

enum BetalningFields {
  'harbetalt' = 'harbetalt',
}

export default class BetalningSection extends SectionClass<IBetalningSectionProps, IBetalningSectionState> {
  constructor(props: IBetalningSectionProps) {
    super(props);

    this.state = {
      harBetalt: false,
      errors: this.getInitialErrorMap([BetalningFields.harbetalt]),
    };
  }

  setBetalningBodyInnerHtml = (html: string) => {
    if (html) html = html.replace('{YRKESDATA_KOSTNAD}', thousands(this.props.kostnad, ' '));
    return { __html: html };
  };

  harBetaltChanged = (field: FormField<boolean>) => {
    const errors = new Map(this.state.errors);
    if (!field.value) {
      errors.delete(FieldName.PaymentDate);
    }

    this.setState({ harBetalt: !!field.value }, () => {
      this.onChangeField({ ...field, value: field.value || null }, errors);
    });
  };

  public render() {
    const {
      Translate,
      openWebAnsokanEpiTexts,
      formType,
      isInEditMode,
      specialistCostInformation,
      skyddadYrkestitelOpenFormEpiTexts,
      activeYrke,
      isOutsideEuEes,
      isAssessment,
      formLanguage,
    } = this.props;
    const { harBetalt } = this.state;

    return (
      <section aria-labelledby="betalning-heading">
        <EpiserverProperty propertyName="BetalningHeading" isInEditMode={isInEditMode}>
          <h2 id="betalning-heading">
            {formType == FormType.SkyddadYrkestitel
              ? skyddadYrkestitelOpenFormEpiTexts.BetalningHeading
              : openWebAnsokanEpiTexts.BetalningHeading}
          </h2>
        </EpiserverProperty>
        <EpiserverProperty propertyName="BetalningPreamble" wrappingElement="span" isInEditMode={isInEditMode}>
          <p className="preamble">
            {formType == FormType.SkyddadYrkestitel
              ? skyddadYrkestitelOpenFormEpiTexts.BetalningPreamble
              : openWebAnsokanEpiTexts.BetalningPreamble}
          </p>
        </EpiserverProperty>
        <EpiserverProperty
          propertyName={
            activeYrke === Yrke.Lakare &&
            formType == FormType.Legitimation &&
            openWebAnsokanEpiTexts.BetalningBodyLakare
              ? 'BetalningBodyLakare'
              : 'BetalningBody'
          }
          usingDangerouslySetInnerHTML={true}
          isInEditMode={isInEditMode}>
          <div
            dangerouslySetInnerHTML={this.setBetalningBodyInnerHtml(
              formType == FormType.SkyddadYrkestitel
                ? skyddadYrkestitelOpenFormEpiTexts.BetalningBody
                : activeYrke === Yrke.Lakare && openWebAnsokanEpiTexts.BetalningBodyLakare
                ? openWebAnsokanEpiTexts.BetalningBodyLakare
                : openWebAnsokanEpiTexts.BetalningBody,
            )}
          />
        </EpiserverProperty>

        {specialistCostInformation && <div dangerouslySetInnerHTML={setInnerHtml(specialistCostInformation)} />}

        <Expander
          heading={
            formType == FormType.SkyddadYrkestitel
              ? skyddadYrkestitelOpenFormEpiTexts.BetalningBoxHeading
              : openWebAnsokanEpiTexts.BetalningBoxHeading
          }
          isExpanded={false}>
          <EpiserverProperty
            propertyName="BetalningBoxBody"
            usingDangerouslySetInnerHTML={true}
            isInEditMode={isInEditMode}>
            <div
              dangerouslySetInnerHTML={setInnerHtml(
                formType == FormType.SkyddadYrkestitel
                  ? skyddadYrkestitelOpenFormEpiTexts.BetalningBoxBody
                  : openWebAnsokanEpiTexts.BetalningBoxBody,
              )}
            />
          </EpiserverProperty>
        </Expander>
        <CheckBox
          fieldName={BetalningFields.harbetalt}
          label={
            Translate(isOutsideEuEes || isAssessment ? 'harbetalatutanforeu' : BetalningFields.harbetalt) +
            ' ' +
            Translate('mandatory')
          }
          onChange={this.harBetaltChanged}
          stringValue={Translate('ja')}
          isRequired={true}
          errorMessage={this.getErrorMessage(BetalningFields.harbetalt)}
        />
        {!harBetalt && <input type="hidden" name="harbetalt" value={Translate('nej')} />}
        {(isOutsideEuEes || isAssessment) && harBetalt && (
          <div className="row m-b-2 m-t-2">
            <div className="col-sm-6">
              <TextInput
                onChange={this.onChangeField}
                errorMessage={this.getErrorMessage(FieldName.PaymentDate)}
                fieldName={FieldName.PaymentDate}
                label={Translate(FieldName.PaymentDate)}
                isRequired={false}
                maxLength={MAX_LENGTH_NUM}
                showMaxLengthMessage={true}
                maxLengthMessageLanguage={formLanguage}
              />
            </div>
          </div>
        )}
        <hr className="formdivider" />
      </section>
    );
  }
}
